import { useMemo, useRef, useEffect } from 'react';

const usePuntosOrientacion = (
  orientacion,
  materiasPrincipalesAprobadas,
  materiasRegistradas,
  materiasObligatorias,
  onPuntosChange
) => {
  const prevCalculatedPuntosRef = useRef(null);

  const calcularPuntos = useMemo(() => {
    if (!orientacion?.materias?.principales) {
      return {
        total: 0,
        orientacionPrincipales: 0,
        orientacionDerivadas: 0,
        constitucionalProfundizado: 0,
        procesalObligatorio: 0,
        integracion: 0,
        practico: 0,
        libresGeneralesCPO: 0,
        filosofia: 0,
        sociales: 0,
        procesalLibre: 0
      };
    }

    let puntosCalculados = {
      orientacionPrincipales: 0,
      orientacionDerivadas: 0,
      constitucionalProfundizado: materiasObligatorias.constitucional ? 4 : 0,
      procesalObligatorio: (materiasObligatorias.metodos ? 2 : 0) + (materiasObligatorias.concursos ? 2 : 0),
      integracion: materiasObligatorias.integracion ? 4 : 0,
      practico: materiasObligatorias.practico ? 19 : 0,
      libresGeneralesCPO: 0,
      filosofia: 0,
      sociales: 0,
      procesalLibre: 0
    };

    // Calcular puntos de materias principales
    Object.entries(materiasPrincipalesAprobadas).forEach(([id, aprobada]) => {
      if (aprobada) {
        const materia = orientacion.materias.principales.find(m => m.id === id);
        if (materia) {
          puntosCalculados.orientacionPrincipales += Number(materia.creditos || 0);
        }
      }
    });

    // Calcular puntos de materias registradas
    puntosCalculados.orientacionDerivadas = materiasRegistradas.derivadas.reduce((sum, materia) => 
      sum + Number(materia.puntos || 0), 0);
    puntosCalculados.libresGeneralesCPO = materiasRegistradas.libresGeneralesCPO.reduce((sum, materia) => 
      sum + Number(materia.puntos || 0), 0);
    puntosCalculados.filosofia = materiasRegistradas.filosofia.reduce((sum, materia) => 
      sum + Number(materia.puntos || 0), 0);
    puntosCalculados.sociales = materiasRegistradas.sociales.reduce((sum, materia) => 
      sum + Number(materia.puntos || 0), 0);
    puntosCalculados.procesalLibre = materiasRegistradas.procesalLibre.reduce((sum, materia) => 
      sum + Number(materia.puntos || 0), 0);

    // Calcular total asegurando que todos los valores sean números
    puntosCalculados.total = Object.values(puntosCalculados).reduce((sum, value) => 
      sum + Number(value || 0), 0);

    return puntosCalculados;
  }, [materiasPrincipalesAprobadas, materiasRegistradas, materiasObligatorias, orientacion]);

  useEffect(() => {
    if (!orientacion) return;

    // Skip the first render
    if (!prevCalculatedPuntosRef.current) {
      prevCalculatedPuntosRef.current = calcularPuntos;
      return;
    }

    const puntosHanCambiado = Object.entries(calcularPuntos).some(([key, value]) => {
      const prevValue = prevCalculatedPuntosRef.current[key];
      return Math.abs(Number(value || 0) - Number(prevValue || 0)) > 0.01;
    });

    if (puntosHanCambiado) {
      // console.debug('OrientacionView - Actualizando puntos:', calcularPuntos);
      prevCalculatedPuntosRef.current = calcularPuntos;
      onPuntosChange(calcularPuntos);
    }
  }, [calcularPuntos, orientacion, onPuntosChange]);

  return calcularPuntos;
};

export default usePuntosOrientacion;
